import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Grid, Paper, Typography } from '@material-ui/core';
import moment from 'moment';
import { useKeycloak } from '@react-keycloak/web';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import QCXFormStepWizard from '../../shared-components/form-step-wizard/QCXFormStepWizard';
import QCXFinalDateTimePickerField from '../../shared-components/final-date-time-picker-field/QCXFinalDateTimePickerField';
import QCXFinalSelectGenericoAutocomplete from '../../shared-components/select-tipo-frete/QCXFinalSelectGenericoAutocomplete';
import QCXFinalTextField from '../../shared-components/final-text-field/QCXFinalTextField';
import QCXButton from '../../shared-components/button/QCXButton';
import QCXFormSubtitle from '../form-title/QCXFormSubtitle';
import {
  addHistoricoStatus,
  fetchHistoricoStatus,
  updateHistoricoStatus,
} from '../../features/catalogo-produtos/catalogoProdutosAPI';
import { setErrorFeedback, setSuccessFeedback } from '../../features/feedback/feedbackSlice';
import {
  MANUAL_STATUS_OPTIONS,
  getStatusLabel,
} from '../../pages/module/registrations/operational/catalogo-produtos/utils/catalogoProdutosHistoricoStatus';

const DATE_FORMAT = 'DD/MM/YYYY HH:mm';

function ReadOnlyCell({ label, value }) {
  return (
    <>
      <Typography variant="caption" color="textSecondary">
        {label}
      </Typography>
      <Typography>{value}</Typography>
    </>
  );
}

function AnaliseConteudo({ form, values, catalogoId }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { keycloak } = useKeycloak();

  const [historico, setHistorico] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const usuarioLogado = useMemo(() => {
    const token = keycloak?.tokenParsed || {};
    const nomeCompleto = `${token.given_name ?? ''} ${token.family_name ?? ''}`.trim();
    return token.name ?? (nomeCompleto || token.preferred_username || '');
  }, [keycloak?.tokenParsed]);

  const carregarHistorico = useCallback(() => {
    if (!catalogoId) {
      setHistorico([]);
      return;
    }
    fetchHistoricoStatus(catalogoId).then((response) => {
      setHistorico(Array.isArray(response.data) ? response.data : []);
    });
  }, [catalogoId]);

  useEffect(() => {
    carregarHistorico();
  }, [carregarHistorico]);

  useEffect(() => {
    if (!values?.analiseNovaData) {
      form.change('analiseNovaData', moment());
    }
    form.change('analiseUsuarioNome', usuarioLogado);
  }, [usuarioLogado]);

  const handleAdicionar = async () => {
    const status = values?.analiseNovoStatus;
    if (!catalogoId || !status) {
      return;
    }
    setSalvando(true);
    try {
      const dataIso = values?.analiseNovaData ? moment(values.analiseNovaData).toISOString() : null;
      await addHistoricoStatus(catalogoId, { status, data: dataIso });

      dispatch(setSuccessFeedback({ message: t('com.muralis.qcx.catalogoProdutos.statusAdicionado') }));

      form.change('analiseNovoStatus', undefined);
      form.change('analiseNovaData', moment());
      carregarHistorico();
    } catch (error) {
      const mensagem = error?.response?.data?.message ?? t('com.muralis.qcx.catalogoProdutos.erroAdicionarStatus');
      dispatch(setErrorFeedback({ message: mensagem }));
    } finally {
      setSalvando(false);
    }
  };

  const handleEditar = (item) => {
    form.change('editData', item.data ? moment(item.data, DATE_FORMAT) : moment());
    form.change('editStatus', item.status);
    setEditingId(item.id);
  };

  const handleCancelarEdicao = () => {
    setEditingId(null);
  };

  const handleSalvarEdicao = async (item) => {
    const status = values?.editStatus;
    if (!status) {
      return;
    }
    setSalvando(true);
    try {
      const dataIso = values?.editData ? moment(values.editData).toISOString() : null;
      await updateHistoricoStatus(catalogoId, item.id, { status, data: dataIso });

      dispatch(setSuccessFeedback({ message: t('com.muralis.qcx.catalogoProdutos.statusAtualizado') }));

      setEditingId(null);
      carregarHistorico();
    } catch (error) {
      const mensagem = error?.response?.data?.message ?? t('com.muralis.qcx.catalogoProdutos.erroEditarStatus');
      dispatch(setErrorFeedback({ message: mensagem }));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Grid item container sm={12}>
      {catalogoId && (
        <Paper style={{ padding: '16px', width: '100%', marginBottom: '1.5rem' }}>
          <QCXFormSubtitle title={t('com.muralis.qcx.catalogoProdutos.adicionarStatus')} />
          <Grid container spacing={2} alignItems="center" style={{ marginTop: '0.5rem' }}>
            <Grid item xs={12} sm={3}>
              <QCXFinalDateTimePickerField name="analiseNovaData" label={t('com.muralis.qcx.data')} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <QCXFinalSelectGenericoAutocomplete
                name="analiseNovoStatus"
                label={t('com.muralis.qcx.status')}
                initialValues={values}
                optionsProp={MANUAL_STATUS_OPTIONS}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <QCXFinalTextField
                name="analiseUsuarioNome"
                label={t('com.muralis.qcx.catalogoProdutos.usuario')}
                disabled
              />
            </Grid>
            <Grid item xs={12} sm={2}>
              <QCXButton
                variant="contained"
                color="secondary"
                onClick={handleAdicionar}
                disabled={!values?.analiseNovoStatus || salvando}
              >
                {t('com.muralis.qcx.acoes.adicionar')}
              </QCXButton>
            </Grid>
          </Grid>
        </Paper>
      )}

      {historico.length === 0 && (
        <Typography>{t('com.muralis.qcx.catalogoProdutos.semHistoricoStatus')}</Typography>
      )}

      {historico.map((item) => {
        const emEdicao = editingId === item.id;
        return (
          <Paper key={item.id} style={{ padding: '16px', width: '100%', marginBottom: '1rem' }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs={12} sm={3}>
                {emEdicao ? (
                  <QCXFinalDateTimePickerField name="editData" label={t('com.muralis.qcx.data')} />
                ) : (
                  <ReadOnlyCell label={t('com.muralis.qcx.data')} value={item.data || '-'} />
                )}
              </Grid>
              <Grid item xs={12} sm={3}>
                {emEdicao ? (
                  <QCXFinalSelectGenericoAutocomplete
                    key={`editStatus-${item.id}`}
                    name="editStatus"
                    label={t('com.muralis.qcx.status')}
                    initialValues={values}
                    optionsProp={MANUAL_STATUS_OPTIONS}
                  />
                ) : (
                  <ReadOnlyCell label={t('com.muralis.qcx.status')} value={getStatusLabel(item.status)} />
                )}
              </Grid>
              <Grid item xs={12} sm={2}>
                <ReadOnlyCell
                  label={t('com.muralis.qcx.catalogoProdutos.usuario')}
                  value={item.usuario || t('com.muralis.qcx.catalogoProdutos.sistema')}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                <ReadOnlyCell
                  label={t('com.muralis.qcx.catalogoProdutos.dataAlteracao')}
                  value={item.dataAlteracao || '-'}
                />
              </Grid>
              <Grid item xs={12} sm={2}>
                {emEdicao ? (
                  <Grid container spacing={1}>
                    <Grid item>
                      <QCXButton
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={() => handleSalvarEdicao(item)}
                        disabled={!values?.editStatus || salvando}
                      >
                        {t('com.muralis.qcx.acoes.salvar')}
                      </QCXButton>
                    </Grid>
                    <Grid item>
                      <QCXButton variant="outlined" size="small" onClick={handleCancelarEdicao} disabled={salvando}>
                        {t('com.muralis.qcx.acoes.cancelar')}
                      </QCXButton>
                    </Grid>
                  </Grid>
                ) : (
                  item.editavel && (
                    <QCXButton
                      variant="contained"
                      color="secondary"
                      size="small"
                      onClick={() => handleEditar(item)}
                      disabled={salvando || editingId !== null}
                    >
                      {t('com.muralis.qcx.acoes.editar')}
                    </QCXButton>
                  )
                )}
              </Grid>
            </Grid>
          </Paper>
        );
      })}
    </Grid>
  );
}

export default function QCXCatalogoProdutosAnaliseManager({
  catalogoId,
  controlComponentProps,
  handleSubmit,
  authInfo = {},
  requiredRoles = [],
  ...restProps
}) {
  return (
    <QCXFormStepWizard
      onSubmit={handleSubmit}
      controlComponentProps={controlComponentProps}
      authInfo={authInfo}
      requiredRoles={requiredRoles}
      disablePristine
      {...restProps}
    >
      {({ form, values }) => <AnaliseConteudo form={form} values={values} catalogoId={catalogoId} />}
    </QCXFormStepWizard>
  );
}
